import { ArrowLeft, ArrowRight, RotateCcw, Home, Save, Download, Info } from 'lucide-react';
import { useState, useCallback } from 'react';
import { Button, ThemeToggle, RecoveryBanner, ConfirmDialog } from '../ui';
import { KeyboardShortcutsHelp } from '../ui/KeyboardShortcutsHelp';
import { useConfigurator } from '../../hooks/useConfigurator';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { useAutosave } from '../../hooks/useAutosave';
import { StepIndicator } from './StepIndicator';
import { HistoryPanel } from '../history';
import { TemplatesPanel } from '../templates';
import {
  ScenarioStep,
  NetworkStep,
  NodesStep,
  ServicesStep,
  SecurityStep,
  SummaryStep,
} from './steps';
import { isStepComplete, WizardStep } from '../../types/configurator';
import { exportConfig, downloadFile } from '../../services/exporter';
import { PRICING_DISCLAIMER } from '../../data/scenarios';

// Link back to main GitLab Pages site
const MAIN_SITE_URL = '../';

export function ConfiguratorWizard() {
  const { currentStep, config, goNext, goBack, goToStep, reset, loadConfig, isDirty } = useConfigurator();
  const [saveNotification, setSaveNotification] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Autosave and recovery
  const {
    hasAutosave,
    autosaveTime,
    restore: restoreAutosave,
    dismiss: dismissAutosave,
  } = useAutosave({
    config,
    isDirty,
    onRestore: loadConfig,
    enabled: true,
  });

  const canGoNext = isStepComplete(currentStep, config);
  const isFirstStep = currentStep === 'scenario';
  const isLastStep = currentStep === 'summary';

  // Save configuration to localStorage
  const handleSave = useCallback(() => {
    try {
      const configJson = JSON.stringify(config, null, 2);
      localStorage.setItem('hermit-config-backup', configJson);
      localStorage.setItem('hermit-config-backup-time', new Date().toISOString());
      setSaveNotification('Configuration saved!');
      setTimeout(() => setSaveNotification(null), 2000);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setSaveNotification('Save failed');
      setTimeout(() => setSaveNotification(null), 2000);
    }
  }, [config]);

  // Export configuration as JSON file
  const handleExport = useCallback(() => {
    try {
      const jsonExport = exportConfig(config, 'json');
      const filename = `hermit-config-${config.name || 'untitled'}-${new Date().toISOString().slice(0, 10)}.json`;
      downloadFile(jsonExport, filename, 'application/json');
      setSaveNotification('Exported!');
      setTimeout(() => setSaveNotification(null), 2000);
    } catch (error) {
      console.error('Failed to export configuration:', error);
      setSaveNotification('Export failed');
      setTimeout(() => setSaveNotification(null), 2000);
    }
  }, [config]);

  // Handle step change from keyboard navigation
  const handleStepChange = useCallback((step: WizardStep) => {
    goToStep(step);
  }, [goToStep]);

  // Use keyboard navigation
  useKeyboardNavigation({
    currentStep,
    config,
    onStepChange: handleStepChange,
    onSave: handleSave,
    onExport: handleExport,
  });

  const renderStep = () => {
    switch (currentStep) {
      case 'scenario':
        return <ScenarioStep />;
      case 'network':
        return <NetworkStep />;
      case 'nodes':
        return <NodesStep />;
      case 'services':
        return <ServicesStep />;
      case 'security':
        return <SecurityStep />;
      case 'summary':
        return <SummaryStep />;
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900 transition-colors">
      {/* Recovery Banner */}
      {hasAutosave && autosaveTime && (
        <RecoveryBanner
          autosaveTime={autosaveTime}
          onRestore={restoreAutosave}
          onDismiss={dismissAutosave}
        />
      )}

      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-4">
            <a
              href={MAIN_SITE_URL}
              className="flex items-center gap-2 rounded-lg px-2 py-1 text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
              title="Back to hermit-μDC documentation"
            >
              <Home className="h-4 w-4" />
              <span className="text-sm">Docs</span>
            </a>
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hermit-600 text-white">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-6 w-6"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="21" x2="9" y2="9" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">hermit-μDC</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Micro-Datacenter Configurator</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Save notification */}
            {saveNotification && (
              <span className="text-sm font-medium text-green-600 dark:text-green-400 animate-pulse">
                {saveNotification}
              </span>
            )}

            {/* Quick save/export buttons */}
            <Button
              variant="ghost"
              onClick={handleSave}
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Save configuration (Ctrl+S)"
            >
              <Save className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              onClick={handleExport}
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Export configuration (Ctrl+E)"
            >
              <Download className="h-4 w-4" />
            </Button>

            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
            <TemplatesPanel />
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
            <HistoryPanel compact />
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
            <KeyboardShortcutsHelp />
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
            <ThemeToggle />
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
            <Button
              variant="ghost"
              onClick={() => setShowResetConfirm(true)}
              className="gap-2 text-gray-500 dark:text-gray-400"
            >
              <RotateCcw className="h-4 w-4" />
              Start Over
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 bg-gray-50 dark:bg-gray-800 px-6 py-8 transition-colors">
        <div className="mx-auto max-w-6xl">
          <StepIndicator />
          {renderStep()}
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4 transition-colors">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Button
            variant="outline"
            onClick={goBack}
            disabled={isFirstStep}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <span>{config.estimatedPower}W | ${config.estimatedCost.toLocaleString()} estimated</span>
            <div className="group relative">
              <Info className="h-3.5 w-3.5 cursor-help text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-gray-900 dark:bg-gray-700 p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
                {PRICING_DISCLAIMER}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
              </div>
            </div>
          </div>

          {!isLastStep && (
            <Button onClick={goNext} disabled={!canGoNext} className="gap-2">
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}

          {isLastStep && (
            <Button onClick={() => window.print()} className="gap-2">
              Print Summary
            </Button>
          )}
        </div>
      </footer>

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={() => {
          reset();
          setShowResetConfirm(false);
        }}
        title="Start Over?"
        message="This will reset all your configuration settings. Any unsaved changes will be lost."
        confirmLabel="Reset Configuration"
        cancelLabel="Keep Working"
        variant="warning"
      />
    </div>
  );
}
